import type { LiveCounter } from "./live-counter";
import { isFreshCompletedCases } from "./operator-completed-cases";

export type TelemetryContext = {
  runId: string;
  terminalCode: string;
  deviceId: string | null;
  source: LiveCounter["source"] | null;
  configured: boolean;
  unit: string | null;
};

export type StableLiveTelemetry = {
  context: TelemetryContext;
  contextCounter: LiveCounter;
  countCounter: LiveCounter | null;
  speedCounter: LiveCounter | null;
  completedCasesCounter?: LiveCounter | null;
  completedCaseRunCounter?: LiveCounter | null;
  integrityIssue: "COUNTER_RESET" | "BASELINE_MISSING" | "GAP_ACROSS_RUN" | null;
};

function integer(value: number | null | undefined): value is number {
  return value != null && Number.isSafeInteger(value) && value >= 0;
}

function sourceOf(counter: LiveCounter): LiveCounter["source"] | null {
  return counter.source ?? null;
}

function unitOf(counter: LiveCounter): string | null {
  return counter.unit?.trim().toLocaleUpperCase("tr-TR") || null;
}

function contextFor(runId: string, terminalCode: string, counter: LiveCounter): TelemetryContext {
  return { runId, terminalCode, deviceId: counter.deviceId, source: sourceOf(counter), configured: counter.configured, unit: unitOf(counter) };
}

function sameContext(left: TelemetryContext, right: TelemetryContext): boolean {
  return left.runId === right.runId && left.terminalCode === right.terminalCode && left.deviceId === right.deviceId &&
    left.source === right.source && left.configured === right.configured && left.unit === right.unit;
}

export function isConfirmedMachineCount(counter: LiveCounter | null): counter is LiveCounter {
  if (!counter || !counter.configured || counter.quality !== "GOOD" || !integer(counter.runQuantity)) return false;
  return counter.source === "LOCAL_PLC_TEST" || unitOf(counter) === "KUTU";
}

export function isFreshConfirmedMachineCount(counter: LiveCounter | null, now: number): counter is LiveCounter {
  if (!isConfirmedMachineCount(counter)) return false;
  const measured = Date.parse(counter.measuredAt ?? "");
  return Number.isFinite(measured) && Math.abs(now - measured) <= 10_000;
}

export function isFreshMachineSpeed(counter: LiveCounter | null, now: number): counter is LiveCounter {
  if (!counter || !counter.configured || !integer(counter.speedPerMinute) || counter.speedPerMinute > 0xffffffff) return false;
  const measured = Date.parse(counter.speedMeasuredAt ?? "");
  return Number.isFinite(measured) && Math.abs(now - measured) <= 10_000;
}

/**
 * Retains only independently valid display readings. The raw counter remains the
 * authority for machine controls; this state is solely for calm dashboard output.
 */
export function reduceStableLiveTelemetry(
  previous: StableLiveTelemetry | null,
  runId: string,
  terminalCode: string,
  counter: LiveCounter | null,
  now: number,
): StableLiveTelemetry | null {
  if (!counter) return previous?.context.runId === runId && previous.context.terminalCode === terminalCode ? previous : null;
  const context = contextFor(runId, terminalCode, counter);
  const state = previous && sameContext(previous.context, context)
    ? previous
    : { context, contextCounter: counter, countCounter: null, speedCounter: null, integrityIssue: null };
  const integrityIssue = counter.quality === "COUNTER_RESET" || counter.quality === "BASELINE_MISSING" || counter.quality === "GAP_ACROSS_RUN"
    ? counter.quality : isFreshConfirmedMachineCount(counter, now) ? null : state.integrityIssue;
  return {
    context,
    contextCounter: {
      ...state.contextCounter,
      ...counter,
      packaging: counter.packaging ?? state.contextCounter.packaging,
    },
    countCounter: isFreshConfirmedMachineCount(counter, now) ? counter : state.countCounter,
    speedCounter: isFreshMachineSpeed(counter, now) ? counter : state.speedCounter,
    completedCasesCounter: isFreshCompletedCases(counter, now) ? counter : state.completedCasesCounter ?? null,
    completedCaseRunCounter: isFreshCompletedCases(counter, now, true) ? counter : state.completedCaseRunCounter ?? null,
    integrityIssue,
  };
}

export function stableCounterForDisplay(state: StableLiveTelemetry | null): LiveCounter | null {
  return state?.contextCounter ?? null;
}

export function stableTelemetryPresentation(
  state: StableLiveTelemetry | null,
  current: LiveCounter | null,
  unavailable: boolean,
  now: number,
  currentStatusLabel: string,
) {
  const countRetained = Boolean(state?.countCounter) && (unavailable || !isFreshConfirmedMachineCount(current, now));
  const speedRetained = Boolean(state?.speedCounter) && (unavailable || !isFreshMachineSpeed(current, now));
  const integrityLabels = {
    COUNTER_RESET: "Sayaç sıfırlandı; iş toplamı doğrulanamıyor",
    BASELINE_MISSING: "İş başlangıcı ölçülmedi; toplam doğrulanamıyor",
    GAP_ACROSS_RUN: "Sayım aralığı eksik; iş toplamı doğrulanamıyor",
  };
  const countLabel = retainedTelemetryLabel("count", countRetained, Boolean(state?.countCounter), now, state?.countCounter ?? null);
  return {
    counter: stableCounterForDisplay(state),
    count: state?.countCounter?.runQuantity ?? null,
    speed: state?.speedCounter?.speedPerMinute ?? null,
    countRetained,
    speedRetained,
    countLabel: state?.integrityIssue
      ? `${integrityLabels[state.integrityIssue]}${state.countCounter ? " · Son doğrulanmış miktar gösteriliyor" : ""}`
      : countRetained && !unavailable ? `${currentStatusLabel} · ${countLabel}` : countLabel,
    speedLabel: retainedTelemetryLabel("speed", speedRetained, Boolean(state?.speedCounter), now, state?.speedCounter ?? null),
  };
}

export function retainedTelemetryLabel(
  kind: "count" | "speed",
  retained: boolean,
  hasValue: boolean,
  now: number,
  counter: LiveCounter | null,
): string {
  if (!retained) {
    if (hasValue) return kind === "speed" ? "Hız ölçümü güncel" : "İş toplamı güncel";
    return kind === "speed" ? "İlk hız ölçümü bekleniyor" : "İlk sayım bekleniyor";
  }
  const measuredAt = kind === "speed" ? counter?.speedMeasuredAt : counter?.measuredAt;
  const age = Date.parse(measuredAt ?? "");
  const seconds = Number.isFinite(age) ? Math.max(0, Math.floor((now - age) / 1000)) : null;
  const suffix = seconds == null ? "son geçerli değer" : `son geçerli değer · ${seconds} sn önce`;
  return kind === "speed" ? `Hız güncellenemedi · ${suffix}` : `Toplam güncellenemedi · ${suffix}`;
}
